import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFieldErrors } from '@/services/api';
import { usePlanQuery } from '../hooks/usePlanQuery';
import { usePlanMutations } from '../hooks/usePlanMutations';
import { PlanForm } from '../components/PlanForm';
import type { PlanFormInput, PlanFormOutput } from '../schemas/plan.schema';
import type { UseFormSetError } from 'react-hook-form';
import { Alert, TextLink } from '@/shared/components';

export function PlanEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const query = usePlanQuery(id ?? '');
    const { updateMutation } = usePlanMutations();
    const setErrorRef = useRef<UseFormSetError<PlanFormInput> | null>(null);

    if (!id) return <p className="text-mp-error">Invalid plan ID.</p>;
    if (query.isLoading) return <p>Loading plan...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Plan not found or failed to load.</span></Alert>;
    }

    const handleSubmit = (data: PlanFormOutput) => {
        updateMutation.mutate(
            { id, payload: { name: data.name, description: data.description, status: data.status, is_looped: data.is_looped } },
            {
                onSuccess: () => navigate(`/smr-admin/plans/${id}`),
                onError: (err: unknown) => {
                    // getFieldErrors reads error.errors first, then the top-level errors compat
                    // key, so this survives slice 3 without a further edit. Always an object,
                    // so the previous field-map presence guard is gone.
                    Object.entries(getFieldErrors(err)).forEach(([field, messages]) => {
                        setErrorRef.current?.(field as keyof PlanFormInput, { message: messages[0] });
                    });
                },
            },
        );
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={`/smr-admin/plans/${id}`} color="secondary" className="text-sm">&larr; Back to Plan</TextLink>
            </div>
            <h1 className="text-2xl font-semibold">Edit: {query.data.name}</h1>
            <PlanForm
                initialValues={{
                    name: query.data.name,
                    description: query.data.description ?? '',
                    status: (query.data.status as 'active' | 'retired') ?? 'active',
                    is_looped: query.data.is_looped ?? false,
                }}
                onSubmit={handleSubmit}
                isLoading={updateMutation.isPending}
                submitLabel="Save Changes"
                onSetErrorRef={(fn) => { setErrorRef.current = fn; }}
            />
        </section>
    );
}
